// aibijia offer → fuelio products 导入 modal。
//
// 跟 zzshu 那条流水的本质区别：
//   - aibijia **不带详情 HTML / 主图** —— admin 需要打开上游 URL 后手工 paste
//   - aibijia **库存只有 in/low/out_of_stock 三档** —— 数字化时是估算（in=10
//     low=2 out=0），所以"自动同步库存"默认关，admin 想开自己开
//   - 链接外键用 offer.url 而不是内部 ULID（aibijia 每次 import ULID 会换）
//
// 我们能预填的：SKU 默认值 / type / name / 价格 / 一段套用 markdown 模板。
// 描述区给出"上游来源 / 价格 / 状态"的元信息片段，下半留空让 admin 写真正
// 介绍文案。

import { useEffect, useState } from 'react'
import { X, ExternalLink } from 'lucide-react'
import {
  api, errorMessage,
  type AibijiaOfferFullRow, type DataSyncAibijiaImportInput,
} from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useToast } from '@/components/ui/Toast'
import { MarkdownPaneEditor } from '@/components/MarkdownPaneEditor'
import { PageError, PageLoading } from './Stats'

type Form = Omit<DataSyncAibijiaImportInput, 'offerId'>

const TYPE_OPTIONS = [
  { value: 'physical' as const, label: '实物 / 账号 (physical)' },
  { value: 'plan'     as const, label: '订阅 / 套餐 (plan)' },
  { value: 'topup'    as const, label: '代充 (topup)' },
]

// 给空白描述区一个 markdown 模板 —— 把"来源平台 / 价格 / 状态"先填好，admin
// 只需要把"# 商品介绍"下面那段写完即可。
function buildTemplate(o: AibijiaOfferFullRow): string {
  const price = o.price_cents !== null ? `¥${(o.price_cents / 100).toFixed(2)}` : '—'
  const statusZh = o.status === 'in_stock' ? '有货'
                 : o.status === 'low_stock' ? '少量'
                 : o.status === 'out_of_stock' ? '缺货'
                 : (o.status ?? '—')
  const platform = o.source_store_name ?? o.platform_name ?? '—'
  const lines = [
    `## 来源信息`,
    ``,
    `- 平台：${platform}`,
    `- 上游价格：${price} ${o.currency ?? ''}`,
    `- 库存状态：${statusZh}`,
    ...(o.url ? [`- 上游链接：[${o.url}](${o.url})`] : []),
    ``,
    `## 商品介绍`,
    ``,
    `_（请打开上游链接，把详情粘贴到这里并美化）_`,
    ``,
  ]
  return lines.join('\n')
}

// 给 SKU 一个默认值：AIBIJIA-<platform>-<short>，其中 short 是 url 末段去
// 特殊字符。例如 https://pay.ldxp.cn/item/t01lix → AIBIJIA-LDXP-T01LIX
function defaultSku(o: AibijiaOfferFullRow): string {
  const platform = (o.platform_name ?? 'X').toUpperCase().replace(/[^A-Z0-9]+/g, '')
  let short = 'X'
  if (o.url) {
    const last = o.url.split('/').filter(Boolean).pop() ?? ''
    short = last.toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 12) || 'X'
  }
  return `AIBIJIA-${platform}-${short}`
}

export function DataSyncAibijiaImportModal({
  offerId,
  onClose,
  onSuccess,
}: {
  offerId: string
  onClose: () => void
  onSuccess: () => void
}) {
  const toast = useToast()
  const [src, setSrc] = useState<AibijiaOfferFullRow | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [form, setForm] = useState<Form | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    api.dataSyncAibijiaOffer(offerId)
      .then((r) => {
        if (!alive) return
        const o = r.offer
        setSrc(o)
        // 优先使用富化数据（enrich-ldxp 等脚本写入），它有真实 markdown + 主图 +
        // 实时价格；没有富化的就退回占位模板，让 admin 手工 paste。
        const hasEnrichment = !!o.enriched_description_md
        setForm({
          sku: defaultSku(o),
          type: 'physical',
          // 富化时上游 name 通常比 aibijia.source_title 更规范（例如 LDXP 把
          // 内部命名"GPT Plus 月卡 成品号"放 name，aibijia 取的是更长的卖点标题）
          nameZh: o.enriched_name?.trim() || o.source_title?.trim() || '',
          nameEn: '',
          // 富化里的 price 更新；fallback aibijia.price_cents
          priceCnyCents: o.enriched_price_cents ?? o.price_cents ?? 0,
          priceUsdCents: null,
          descriptionMd: hasEnrichment ? o.enriched_description_md! : buildTemplate(o),
          active: true,
          autoSyncPrice: true,
          autoSyncStock: false,    // aibijia status 不够精确，默认关
          coverUrl: o.enriched_cover_url ?? undefined,
        })
      })
      .catch((e) => { if (alive) setLoadErr(errorMessage(e)) })
    return () => { alive = false }
  }, [offerId])

  if (loadErr) return <ModalShell onClose={onClose} title={`导入 aibijia#${offerId}`}>
    <PageError msg={loadErr}/>
  </ModalShell>
  if (!src || !form) return <ModalShell onClose={onClose} title={`导入 aibijia#${offerId}`}>
    <PageLoading/>
  </ModalShell>

  function update<K extends keyof Form>(k: K, v: Form[K]) {
    setForm((prev) => prev ? { ...prev, [k]: v } : prev)
  }

  async function submit() {
    if (!form) return
    setBusy(true); setErr(null)
    try {
      const r = await api.dataSyncAibijiaImport({ offerId, ...form })
      toast.success(`已导入 ${r.sku}`)
      onSuccess()
    } catch (e) {
      setErr(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell onClose={onClose} title={`导入 aibijia offer → fuelio products`}>
      {err && (
        <div className="mb-3 px-3 py-2 rounded bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {err}
        </div>
      )}

      {/* 富化感知横幅：有 enrichment 数据 → 绿色"已抓取详情"；没有 → 黄色提醒手工 paste */}
      {src.enriched_description_md ? (
        <div className="mb-4 px-3 py-2 rounded bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-800 dark:text-emerald-300">
          ✅ 已从 <span className="font-mono uppercase">{src.enriched_source ?? 'upstream'}</span> 抓取详情（{src.enriched_description_md.length} 字 markdown + 主图）。
          {src.enriched_fetched_at && <span className="ml-1 opacity-80">抓取于 {new Date(src.enriched_fetched_at * 1000).toLocaleString('zh-CN')}</span>}
        </div>
      ) : (
        <div className="mb-4 px-3 py-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
          ⚠ 这条 offer 还没"富化"。aibijia 本身只有聚合数据，没有详情 HTML / 主图。
          想自动预填，去 <code>crawlers/aibijia</code> 跑 <code>pnpm enrich:ldxp:local</code>（Mac 本地）或 remote（JD 云）。
          否则请点"打开上游"看商品页 → 手工 paste 描述。
          {src.url && (
            <a href={src.url} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex items-center gap-1 underline">
              打开上游 <ExternalLink className="w-3 h-3"/>
            </a>
          )}
        </div>
      )}

      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="SKU（唯一）" hint="默认 AIBIJIA-<平台>-<URL尾段>">
            <Input
              value={form.sku}
              onChange={(e) => update('sku', e.target.value.toUpperCase())}
              className="font-mono"
            />
          </Field>
          <Field label="商品类型">
            <select
              value={form.type}
              onChange={(e) => update('type', e.target.value as Form['type'])}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
            >
              {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="名称 (中文)" hint="预填 aibijia 的 source_title，可改">
          <Input value={form.nameZh} onChange={(e) => update('nameZh', e.target.value)}/>
        </Field>
        <Field label="名称 (英文)" hint="留空则 en 站回退用中文">
          <Input value={form.nameEn ?? ''} onChange={(e) => update('nameEn', e.target.value)}/>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label="价格 CNY (元)"
            hint={`上游 ${src.platform_name ?? '—'} 当前 ¥${((src.price_cents ?? 0) / 100).toFixed(2)}`}
          >
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">¥</span>
              <Input
                type="number" step="0.01" min="0"
                value={(form.priceCnyCents / 100).toString()}
                onChange={(e) => {
                  const y = parseFloat(e.target.value || '0')
                  if (Number.isFinite(y)) update('priceCnyCents', Math.max(0, Math.round(y * 100)))
                }}
                className="font-mono pl-7"
              />
            </div>
          </Field>
          <Field label="价格 USD (元)" hint="可选；留空 = 不设 USD 价">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                type="number" step="0.01" min="0"
                value={form.priceUsdCents !== null && form.priceUsdCents !== undefined ? (form.priceUsdCents / 100).toString() : ''}
                onChange={(e) => {
                  if (e.target.value === '') return update('priceUsdCents', null)
                  const d = parseFloat(e.target.value)
                  if (Number.isFinite(d)) update('priceUsdCents', Math.max(0, Math.round(d * 100)))
                }}
                placeholder="—"
                className="font-mono pl-7"
              />
            </div>
          </Field>
        </div>

        <Field label="主图 URL" hint="aibijia 没有，请手填（可贴 zzshu/ldxp/R2 等任意 URL）">
          <Input
            value={form.coverUrl ?? ''}
            onChange={(e) => update('coverUrl', e.target.value || undefined)}
            placeholder="https://..."
          />
        </Field>

        <MarkdownPaneEditor
          label="宝贝详情"
          value={form.descriptionMd}
          onChange={(v) => update('descriptionMd', v)}
          rows={16}
          fixedHeight={400}
          initialMode="split"
          hint={`已套模板 · ${form.descriptionMd.length} 字`}
          placeholder="# 标题…"
        />

        <div className="flex flex-wrap gap-4 items-center">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => update('active', e.target.checked)}
            />
            立即上架 (active)
          </label>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.autoSyncPrice}
              onChange={(e) => update('autoSyncPrice', e.target.checked)}
            />
            后续"更新"覆盖价格
          </label>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer" title="aibijia 库存只有三档，准确度低，默认关">
            <input
              type="checkbox"
              checked={form.autoSyncStock}
              onChange={(e) => update('autoSyncStock', e.target.checked)}
            />
            后续"更新"覆盖库存（估算）
          </label>
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3 mt-2">
          <div className="text-xs text-muted-foreground">
            链接外键用 url：<code className="font-mono">{src.url ?? '—'}</code>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>取消</Button>
            <Button onClick={() => void submit()} disabled={busy}>
              {busy ? '保存中…' : '保存到 fuelio products'}
            </Button>
          </div>
        </div>
      </div>
    </ModalShell>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-xs font-medium text-foreground">{label}</label>
        {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  )
}

function ModalShell({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div className="bg-card border border-border rounded-lg max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="font-semibold text-sm">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5"/>
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}
